// ═══════════════════════════════════════════════════════════
// Rate Limits, Batch Sizes, and App Constraints
// ═══════════════════════════════════════════════════════════

/** Bitrix24 API rate limit: requests per second (standard SMB plan) */
export const B24_RATE_LIMIT_RPS = 2;

/** Maximum sub-calls in a single B24 batch request */
export const B24_BATCH_SIZE = 50;

/** Default page size for B24 entity.item.get */
export const B24_DEFAULT_PAGE_SIZE = 50;

/** Maximum page size for B24 list requests */
export const B24_MAX_PAGE_SIZE = 50;

/** OAuth refresh buffer: refresh token X ms before expiry */
export const OAUTH_REFRESH_BUFFER_MS = 30_000;

/** OAuth refresh lock TTL in milliseconds */
export const OAUTH_LOCK_TTL_MS = 10_000;

/** Maximum formula expression length (DoS protection) */
export const MAX_EXPRESSION_LENGTH = 500;

/** Maximum AST parsing depth (stack overflow protection) */
export const MAX_AST_DEPTH = 20;

/** Maximum numeric result (overflow guard) */
export const MAX_RESULT = 1e15;

/** Division by zero returns this value */
export const SAFE_DIVISION_RESULT = 0;

/** Data Storage entity key for commission journal */
export const JOURNAL_ENTITY_KEY = "cmn_journal";

/** Data Storage entity key for rule metadata */
export const RULES_ENTITY_KEY = "cmn_rules";

/** Data Storage entity key for pre-aggregated metrics */
export const TOTALS_ENTITY_KEY = "cmn_totals";

/** Maximum property key length in Data Storage */
export const MAX_PROPERTY_KEY_LENGTH = 13;

/** Default trial duration in days */
export const TRIAL_DURATION_DAYS = 14;

/** Number of seconds in one day */
export const SECONDS_PER_DAY = 86_400;

/** Default OAuth token lifetime in seconds (used when B24 omits expires_in) */
export const DEFAULT_TOKEN_EXPIRY_SECONDS = 3_600;

/** How often to re-bind ONAPPINSTALL/ONAPPUNINSTALL lifecycle events (7 days in seconds) */
export const LIFECYCLE_REBIND_WINDOW_SECONDS = 7 * 24 * 60 * 60;

/**
 * How often the boot safety check re-verifies rule-driven CRM webhook events
 * (ONCRMDEALUPDATE etc.) against LIVE B24 state and auto-heals drift. Shorter
 * than the lifecycle window — a missing CRM event means commissions silently
 * stop calculating, so this needs to self-correct fast. The hourly cron
 * sweep (src/worker/index.ts) covers idle tenants; this covers active ones
 * the moment someone opens the app.
 */
export const WEBHOOK_EVENTS_CHECK_WINDOW_SECONDS = 60 * 60;

/** Default clawback window in days */
export const DEFAULT_CLAWBACK_WINDOW_DAYS = 90;

/** Clawback distributed-lock TTL in seconds */
export const CLAWBACK_LOCK_TTL_SECONDS = 120;

/** Default fiscal period type used when no tenant setting is present */
export const DEFAULT_FISCAL_PERIOD_TYPE = "MONTHLY";

/** Queue retry: max attempts before marking as DEAD */
export const QUEUE_MAX_ATTEMPTS = 3;

/** Queue retry: maximum backoff delay in seconds */
export const QUEUE_MAX_BACKOFF_SECONDS = 60;

// ─── Historical Sync (backfill) ─────────────────────────────
// A backfill shares COMMISSION_QUEUE with live webhook processing, so its
// whole pacing design exists to bound queue depth: enqueue one page of work,
// then wait roughly as long as that page takes to drain before enqueueing the
// next. Live commissions therefore never wait more than about one page.

/** Entities enumerated per HISTORICAL_SYNC page. Matches B24's list page size. */
export const HISTORICAL_SYNC_PAGE_SIZE = 50;

/**
 * Delay before the next page is enqueued. Sized to how long one page takes to
 * drain: each item costs ~2 B24 calls (crm.item.get + productrow.list) at
 * B24_RATE_LIMIT_RPS, so 50 items ≈ 50s. Slightly under that keeps the
 * consumer fed without letting the backlog grow unboundedly ahead of it.
 */
export const HISTORICAL_SYNC_PAGE_DELAY_SECONDS = 45;

/**
 * Hard ceiling on entities enumerated by a single job. Reaching it stops
 * pagination and marks the job CAPPED — a visible, resumable stop rather than
 * a run that quietly consumes a portal's API budget for a day.
 */
export const HISTORICAL_SYNC_MAX_ITEMS = 100_000;

/** Maximum pages to fetch when listing all active rules during webhook processing (50 rules/page → 500 rules max) */
export const MAX_RULES_PAGES = 10;

/** Default pagination for frontend data tables */
export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/** Default currency code (ISO 4217) used when no portal currency is configured */
export const DEFAULT_CURRENCY = "USD";

/** License grace period — days after expiry before features are hard-blocked */
export const GRACE_PERIOD_DAYS = 3;

/** Confirmation text required for irreversible data deletion operations */
export const DELETION_CONFIRM_TEXT = "DELETE ALL";

/** Commission rule priority bounds */
export const RULE_PRIORITY_MIN = 1;
export const RULE_PRIORITY_MAX = 999;

/** Supported formula functions */
export const FORMULA_FUNCTIONS = ["MIN", "MAX", "ABS", "ROUND", "FLOOR", "CEIL", "IF"] as const;

/**
 * Current required Data Storage schema version.
 *
 * Bump this integer whenever JOURNAL_PROPERTIES gains or loses a property.
 * Phase 4: cmn_journal reduced to 7 snapshot-only properties (d1_jnl_id,
 * formula_snap, input_snap, rep_snap, product_snap, approval_snap, fx_snap).
 * Rule metadata (cmn_rules) and totals (cmn_totals) are no longer managed
 * in B24 Data Storage — any tenant whose D1 `storage_schema_version` is
 * NULL or less than this value will be flagged by the admin banner and
 * auto-repaired by the hourly cron.
 *
 * Convention: create a migration and bump this constant in the SAME PR.
 */
export const CURRENT_STORAGE_SCHEMA_VERSION = 5;
